import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function ScreenTenant() {
  const [form, setForm] = useState({ tenant_email: '', tenant_name: '', rental_address: '', rental_unit: '', rent_amount: '' });
  const [payer, setPayer] = useState('landlord'); // 'landlord' or 'tenant'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const token = localStorage.getItem('token');
  const userType = localStorage.getItem('userType');

  if (!token || userType !== 'landlord') {
    return (
      <main className="App-main">
        <div style={{ maxWidth: 480, margin: '3rem auto', textAlign: 'center' }}>
          <h2>Screen a tenant</h2>
          <p className="status-text" style={{ marginBottom: '1.5rem' }}>Sign in or create an account to get started.</p>
          <Link to="/register?intent=screen" className="primary-button" style={{ textDecoration: 'none', display: 'inline-block' }}>Create account</Link>
          <p className="status-text" style={{ marginTop: '1rem' }}>Already have an account? <Link to="/login">Log in</Link></p>
        </div>
      </main>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/screening/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          tenant_email: form.tenant_email.trim(),
          tenant_name: form.tenant_name.trim() || null,
          rental_address: form.rental_address.trim(),
          rental_unit: form.rental_unit.trim() || null,
          rent_amount: form.rent_amount ? parseFloat(form.rent_amount) : null,
          payer,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.email_unverified) {
          setError('Please verify your email first. Check your inbox for a verification link.');
        } else if (data.identity_unverified) {
          setError('Identity verification is required before screening tenants. Complete the verification in your dashboard.');
        } else {
          throw new Error(data.error || 'Failed to create screening request');
        }
        return;
      }
      if (payer === 'tenant') {
        // Tenant pays — invite sent, show confirmation
        setSent(true);
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  if (sent) {
    return (
      <main className="App-main">
        <div style={{ maxWidth: 480, margin: '3rem auto', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>✉️</div>
          <h2 style={{ marginBottom: '0.5rem' }}>Screening invite sent</h2>
          <p className="status-text" style={{ marginBottom: '1rem' }}>
            We've emailed <strong>{form.tenant_email}</strong> with a link to complete the screening and pay the $29 report fee.
          </p>
          <p className="status-text" style={{ marginBottom: '1.5rem', fontSize: '0.85rem' }}>
            You'll be notified when the report is ready.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="primary-button" onClick={() => { setForm({ tenant_email: '', tenant_name: '', rental_address: '', rental_unit: '', rent_amount: '' }); setSent(false); }}>Screen another tenant</button>
            <Link to="/landlord/screenings" className="secondary-button" style={{ textDecoration: 'none' }}>View screenings</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="App-main">
      <div style={{ maxWidth: 520, margin: '2rem auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ marginBottom: '0.35rem' }}>Screen a tenant</h2>
          <p className="status-text">Enter your tenant's details. They'll receive a link to complete the screening.</p>
        </div>

        <form onSubmit={handleSubmit} className="property-form">
          {error && <p className="error-message">{error}</p>}

          <div className="form-group">
            <label htmlFor="tenant_email">Tenant's email *</label>
            <input type="email" id="tenant_email" name="tenant_email" value={form.tenant_email} onChange={handleChange} required placeholder="tenant@example.com" />
          </div>

          <div className="form-group">
            <label htmlFor="tenant_name">Tenant's name</label>
            <input type="text" id="tenant_name" name="tenant_name" value={form.tenant_name} onChange={handleChange} placeholder="John Smith" />
          </div>

          <div className="form-group">
            <label htmlFor="rental_address">Rental address *</label>
            <input type="text" id="rental_address" name="rental_address" value={form.rental_address} onChange={handleChange} required placeholder="123 Main St, Toronto, ON" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label htmlFor="rental_unit">Unit (optional)</label>
              <input type="text" id="rental_unit" name="rental_unit" value={form.rental_unit} onChange={handleChange} placeholder="Unit 2" />
            </div>
            <div className="form-group">
              <label htmlFor="rent_amount">Monthly rent (optional)</label>
              <input type="number" id="rent_amount" name="rent_amount" value={form.rent_amount} onChange={handleChange} placeholder="2000" min="0" step="0.01" />
            </div>
          </div>

          {/* ── Who pays? ── */}
          <div style={{ marginTop: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>Who pays for the report?</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[
                { value: 'landlord', label: "I'll pay", desc: 'Pay now and send invite' },
                { value: 'tenant', label: 'Tenant pays', desc: 'Tenant pays before submitting' },
              ].map(opt => {
                const selected = payer === opt.value;
                return (
                  <button key={opt.value} type="button" onClick={() => setPayer(opt.value)} style={{
                    padding: '0.75rem', borderRadius: '10px', cursor: 'pointer', textAlign: 'center',
                    border: selected ? '2px solid var(--accent-color, #28b6c8)' : '1px solid var(--border-color, #e5e7eb)',
                    background: selected ? 'rgba(40,182,200,0.1)' : 'var(--card-bg, var(--surface-color, #f9fafb))',
                    color: 'var(--text-color, #1e293b)',
                    transition: 'all 0.15s'
                  }}>
                    <strong style={{ display: 'block', fontSize: '0.9rem', color: selected ? 'var(--accent-color, #28b6c8)' : 'var(--text-color, #1e293b)' }}>{selected ? '✓ ' : ''}{opt.label}</strong>
                    <span style={{ fontSize: '0.78rem', color: 'var(--light-text-color, #64748b)' }}>{opt.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{
            marginTop: '1rem', padding: '1rem 1.25rem', borderRadius: '10px',
            background: 'var(--surface-color, #f9fafb)', border: '1px solid var(--border-color, #e5e7eb)',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0 0 0.25rem', fontWeight: 600, fontSize: '1.1rem' }}>$29 per report</p>
            <p className="status-text" style={{ margin: 0, fontSize: '0.82rem' }}>
              Includes Credit check + AI risk assessment.
              {payer === 'landlord' ? <><br />Pro members pay $25/report. <Link to="/pricing">Learn more</Link></> : <><br />Your tenant will pay $29 before submitting.</>}
            </p>
          </div>

          <button type="submit" className="primary-button" disabled={loading} style={{ width: '100%', marginTop: '1.25rem', justifyContent: 'center' }}>
            {loading ? 'Sending...' : payer === 'landlord' ? 'Pay & send invite — $29' : 'Send screening invite'}
          </button>

          <p className="status-text" style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.8rem' }}>
            {payer === 'landlord'
              ? 'Your tenant will receive an email to complete a short form and consent to a soft credit check.'
              : 'Your tenant will receive an email to pay, complete a short form, and consent to a soft credit check.'}
          </p>
        </form>
      </div>
    </main>
  );
}

export default ScreenTenant;
